/*
 * admin-overview.js — System status band panel controller.
 *
 * EXTRACTED VERBATIM from admin-dashboard.js (lines 254-335 of the pre-split
 * file). Not one line of the panel body was rewritten: the shell's helpers are
 * re-bound as local vars at the top of init(), so every call site inside reads
 * exactly as it did before. That is deliberate. The split is structural and
 * nothing about behaviour was allowed to change with it.
 *
 * Loaded by admin-dashboard.js via dynamic import() the first time the
 * "overview" tab is activated, resolved through window.CIRCADIA_ADMIN_ASSETS
 * which sections/page-admin.liquid emits (a .js file cannot call asset_url).
 *
 * init(api) is called exactly once. api carries the shell's shared surface.
 */
export function init(api) {
  'use strict';
  var adminFetch = api.adminFetch;
  var escapeHtml = api.escapeHtml;
  var adminHttpMessage = api.adminHttpMessage;
  var adminRejectMessage = api.adminRejectMessage;
  // ─── System status band (Control room, first slice of the redesign) ────
  //
  // Four tiles from ONE GET /admin/system-status. One endpoint rather than four
  // because the band sits on the landing tab and four round trips for one strip
  // of text is a bad trade. Lazy-init on first overview show, same as every tool
  // below, so it costs nothing until the admin actually opens.
  //
  // The band NEVER blocks the page. A failed fetch leaves a quiet line; a tile
  // whose own query failed is reported by the server in `unavailable` and shown
  // as unavailable rather than as a healthy zero, because a status band that
  // reads "All healthy" because it could not look is worse than no band.
  // NO trailing slash: the route is mounted at the sub-router root, matching the
  // RV_BASE comment at the reviews tool.
  var ST_BASE = '/apps/dashboard/admin/system-status';
  var statusBandInited = false;

  function maybeInitStatusBand() {
    if (statusBandInited) return;
    var root = document.querySelector('[data-status-root]');
    if (!root) return;
    statusBandInited = true;
    loadStatusBand();
  }

  // THE DESIGN'S STAT TILE (.adm-stat), not the hand-rolled one. Markup only:
  // the tone the server sends, the unavailable list and every string are
  // unchanged from the pre-design version.
  //
  // The tone lands on the VALUE, which is how the design carries state — a
  // coloured number rather than a dot beside a label. `ok` deliberately takes
  // NO tone class: the design's is-good is green, and green means good, while
  // most of these tiles are neutral counts that are not good news, just news.
  // A failed query is is-bad, because a tile that cannot look is not healthy.
  // WHERE EACH TILE GOES (batch 12, A22). The design's heartbeat cells
  // navigate to the tool that owns them (admin-overview.jsx:425-433). Jobs and
  // webhooks live on System health; stock's nearest built home is the meter.
  // Privacy has NO destination - the tool is not built - so that tile stays
  // inert with the reason in its title, rather than routing somewhere wrong.
  var TILE_TARGETS = { jobs: 'health', webhooks: 'health', stock: 'system-meter' };

  // AN INERT TILE EXPLAINS ITSELF, AND EACH ONE HAS ITS OWN REASON.
  // This used to be a single hardcoded title on the fallback branch, written
  // when privacy was the only target-less tile. The step-up kill-switch tile
  // is target-less too and sorts FIRST in the band, so it inherited privacy's
  // sentence: a tile added specifically to be loud about a disabled security
  // control explained itself with a line about an unrelated unbuilt feature.
  // A tile with no entry here now gets no title at all - saying nothing is
  // honest, saying the wrong thing is not.
  var TILE_INERT_REASON = {
    privacy: 'The privacy tool is not built yet.',
    step_up: 'Set in Railway, not in the app. See docs/06_INFRASTRUCTURE/LAUNCH_CHECKLIST.md.'
  };

  function statusTileHtml(tile, unavailable) {
    var isDown = unavailable.indexOf(tile.key) >= 0;
    var tone = isDown ? ' is-bad' : tile.tone === 'warn' ? ' is-warn' : '';
    var target = TILE_TARGETS[tile.key];
    // The sub line is drawn clamped to two lines (the design's heartbeat), so a long
    // one also carries its full text as a title.
    var sub = isDown ? 'The query for this tile failed.' : String(tile.sub || '');
    var inner =
      '<span class="adm-stat-label">' + escapeHtml(tile.label) + '</span>' +
      '<span class="adm-stat-value' + tone + '">' +
      (isDown ? 'Unavailable' : escapeHtml(tile.value)) +
      '</span>' +
      '<span class="adm-stat-sub"' + (sub.length > 44 ? ' title="' + escapeHtml(sub) + '"' : '') + '>' +
      escapeHtml(sub) +
      '</span>';
    // The tone is also on the tile itself: the status dot reads it (admin-parity.css, 9a).
    if (target) {
      return '<button type="button" class="adm-stat adm-stat-link' + tone + '" data-tab-button="' + target + '">' + inner + '</button>';
    }
    var reason = TILE_INERT_REASON[tile.key];
    return (
      '<div class="adm-stat' + tone + '"' +
      (reason ? ' title="' + escapeHtml(reason) + '"' : '') +
      '>' + inner + '</div>'
    );
  }

  // THE CONTROL-ROOM RACE, 2026-09-18. loadStatusBand() re-issues a brand
  // new, uncancelled GET every time the Overview tab (re)activates - there
  // is no AbortController and no request-sequence check anywhere below. Two
  // in-flight calls (leave Overview, come back before the first resolves)
  // race on ordinary network latency, and whichever RESPONSE ARRIVES LAST
  // wins band.innerHTML - not whichever REQUEST WAS ISSUED LAST. A slower,
  // now-stale response can overwrite an already-correct, fresher render,
  // which is exactly "values in the wrong slot mid-resolve, settles once
  // the page finishes loading" (it self-corrects only because no further
  // requests stay outstanding after that). statusBandRequestToken is
  // incremented on every call; each call's own .then() chain checks it is
  // STILL the most recent call before writing anything to the DOM, so a
  // stale response is silently discarded instead of applied.
  var statusBandRequestToken = 0;

  function loadStatusBand() {
    var band = document.querySelector('[data-status-band]');
    if (!band) return;
    var myToken = ++statusBandRequestToken;
    adminFetch(ST_BASE)
      .then(function (res) {
        if (myToken !== statusBandRequestToken) return null; // superseded — discard
        // adminFetch ALREADY parsed the body. There is no .json() on this value;
        // calling one threw a TypeError that landed in the catch below and showed
        // "unavailable" on a perfectly good 200. Same mistake was in three other
        // places in this file.
        if (!res.ok) {
          band.innerHTML =
            '<p class="admin-status-loading">' + escapeHtml(adminHttpMessage(res)) + '</p>';
          return null;
        }
        return res.data;
      })
      .then(function (data) {
        if (data === null) return;
        if (myToken !== statusBandRequestToken) return; // superseded — discard
        var tiles = (data && data.tiles) || [];
        var unavailable = (data && data.unavailable) || [];
        if (!tiles.length) {
          band.innerHTML = '<p class="admin-status-loading">No status to report.</p>';
          return;
        }
        band.innerHTML = tiles
          .map(function (t) {
            return statusTileHtml(t, unavailable);
          })
          .join('');

        // THE ONE LIVE BADGE, from data already on the wire. system-status is the
        // only endpoint this admin has that reports things NEEDING ATTENTION, and
        // this panel is already fetching it, so the Company badge costs no extra
        // request and no handler change. It counts tiles that are degraded or
        // whose own query failed - a real number with a real meaning, not a
        // decoration. Every other group's badge stays a flagged placeholder until
        // its handler returns a count.
        if (typeof api.setBadges === 'function') {
          var attention = tiles.filter(function (t) {
            return t.tone === 'warn' || unavailable.indexOf(t.key) >= 0;
          });
          // The badge carries its own reason. A bare count on a nav group is
          // an alarm with no subject: the reader learns something is wrong and
          // has to hunt for what.
          var why = attention
            .map(function (t) { return t.label; })
            .join(', ');
          api.setBadges(
            { 'audit-log': attention.length },
            { 'audit-log': why ? 'Needs attention: ' + why : '' }
          );
        }
      })
      .catch(function (err) {
        if (myToken !== statusBandRequestToken) return; // superseded — discard
        // Deliberately quiet. The band is diagnostic furniture on the landing
        // tab; a loud error here would be the first thing seen on every admin
        // load whenever the endpoint is briefly unreachable.
        band.innerHTML =
          '<p class="admin-status-loading">' + escapeHtml(adminRejectMessage(err)) + '</p>';
      });
  }

  maybeInitStatusBand();

  // ─── THE KPI BAND (design batch 07, AdmKpiBand) ───────────────────────────
  //
  // Slots come from the metric REGISTRY (batch 06) - the band never computes a
  // number of its own. The design's defaults are kept verbatim: cash, burn,
  // sessions-30d, audience, preorder-value (admin-data-metrics.js:309). Two of
  // those are live today and carry real charts from the money-home month
  // series; three are launch-gated and render the honest waiting state, which
  // is exactly what a truthful landing screen looks like pre-launch.
  //
  // The design's six chart modes need each metric's HISTORY, and the registry
  // has no series contract yet - so a chart renders only where a real series
  // exists (cash: monthly closing, spark; burn: monthly spend, spark). No
  // series, no chart: a number without history is a number, not a flat line.
  var BAND_KEY = 'circadia.controlroom.band';
  var BAND_DEFAULTS = ['cash', 'burn', 'sessions-30d', 'audience', 'preorder-value'];
  var bandData = null; // { metrics: [...], months: [...] }

  function bandSlots() {
    try {
      var raw = window.localStorage.getItem(BAND_KEY);
      if (raw) {
        var arr = JSON.parse(raw);
        if (Array.isArray(arr) && arr.length >= 1 && arr.length <= 8) return arr;
      }
    } catch (_) {}
    return BAND_DEFAULTS.slice();
  }
  function saveSlots(slots) {
    try { window.localStorage.setItem(BAND_KEY, JSON.stringify(slots)); } catch (_) {}
  }

  // Batch 16: the series ride the metrics read itself now (the series
  // contract) - any available metric with history sparks, not just the two
  // this file used to special-case off a second read.
  function seriesFor(id) {
    var m = metricById(id);
    if (!m || !m.series || !m.series.length) return null;
    return m.series.map(function (p) { return { v: p.v }; });
  }

  function metricById(id) {
    var hit = null;
    (bandData.metrics || []).forEach(function (m) { if (m.id === id) hit = m; });
    return hit;
  }

  // The tool a cell opens: the metric's own, when this admin has that tool. A route with
  // no panel is not offered as a link (the click would do nothing), so that cell is drawn
  // as plain figures.
  function routeOf(m) {
    var r = m && m.route;
    return r && document.querySelector('[data-tab-panel="' + r + '"]') ? String(r) : '';
  }

  // One cell of the band (the design's AdmOvKpi): the label, the figure, the chart where a
  // real series exists, the caption. No value yet is an em dash and the reason, never a number.
  function kpiCellHtml(id, index) {
    var m = metricById(id);
    var CH = window.CIRCADIA_CHARTS;
    var label, value, valueCls = '', sub, chart = '';
    if (!m) {
      label = id;
      value = '&mdash;';
      valueCls = ' is-dim';
      sub = 'Not in the registry.';
    } else if (m.available) {
      label = m.label;
      value = escapeHtml(m.display);
      valueCls = m.tone === 'warn' ? ' is-warn' : '';
      sub = m.sub || '';
      var series = seriesFor(id);
      if (series && CH) {
        chart = CH.spark(series, { label: m.label + ' by month', height: 34, money: true });
      }
    } else {
      label = m.label;
      value = '&mdash;';
      valueCls = ' is-dim';
      sub = m.reason || 'Waiting on a data source.';
    }
    var face =
      '<span class="adm-ov-kpi-label">' + escapeHtml(label) + '</span>' +
      '<span class="adm-ov-kpi-value' + valueCls + '">' + value + '</span>' +
      chart +
      '<span class="adm-ov-kpi-sub">' + escapeHtml(sub) + '</span>';
    var route = routeOf(m);
    var cell = route
      ? '<button type="button" class="adm-ov-kpi" data-tab-button="' + escapeHtml(route) + '">' + face + '</button>'
      : '<div class="adm-ov-kpi is-static">' + face + '</div>';
    // The swap control (batch 12, A22): a SEARCHABLE picker, because a bare select is
    // unusable at 129 options. As in the design it is a popover set against this button
    // (the band clips its cells), opened by the click handler below.
    var swap = bandData && bandData.metrics
      ? '<button type="button" class="adm-ov-kpi-swap" data-kpi-swap-btn="' + index + '" title="Swap this metric" aria-haspopup="dialog" ' +
        'aria-label="Change this slot: ' + escapeHtml(label) + '">&#8645;</button>'
      : '';
    return '<div class="adm-ov-kpi is-cfg">' + cell + swap + '</div>';
  }

  function pickListHtml(query) {
    var q = (query || '').toLowerCase();
    var groups = {};
    (bandData.metrics || []).forEach(function (x) {
      if (q && (x.label + ' ' + x.id + ' ' + x.group).toLowerCase().indexOf(q) < 0) return;
      (groups[x.group] = groups[x.group] || []).push(x);
    });
    var h = '<div data-kpi-picklist>';
    var any = false;
    Object.keys(groups).forEach(function (g) {
      h += '<button type="button" class="pk-group" disabled>' + escapeHtml(g) + '</button>';
      groups[g].forEach(function (x) {
        any = true;
        h += '<button type="button" data-kpi-pick="' + escapeHtml(x.id) + '"><span>' + escapeHtml(x.label) + '</span>' +
          '<em>' + (x.available ? escapeHtml(x.display || '') : 'waiting') + '</em></button>';
      });
    });
    if (!any) h += '<button type="button" disabled>Nothing matches.</button>';
    return h + '</div>';
  }

  function renderBand() {
    var box = document.querySelector('[data-kpi-band]');
    if (!box || !bandData) return;
    closePicker();
    var slots = bandSlots();
    var h = '<div class="adm-bandwrap"><div class="adm-ov-kpis">';
    slots.forEach(function (id, i) { h += kpiCellHtml(id, i); });
    h += '</div><div class="adm-bandfoot">';
    if (slots.length < 8) {
      h += '<button type="button" class="adm-bandadd" data-kpi-add>+ Add a metric</button>';
    }
    h += '<button type="button" class="adm-ov-resetslots" data-tab-button="metrics">Explore all ' +
      (bandData.metrics ? bandData.metrics.length : '') + ' metrics</button>';
    // Offered only when the band is not already the default (the design's rule).
    if (slots.join() !== BAND_DEFAULTS.join()) {
      h += '<button type="button" class="adm-ov-resetslots" data-kpi-reset>Reset to default</button>';
    }
    h += '<span class="adm-bandnote">' + slots.length + ' of 8 slots. Choices stay in this browser.</span>' +
      '</div></div>';
    box.innerHTML = h;
  }

  // The picker popover (design AdmMetricPicker): fixed against the swap button's rect so the
  // band's clipping cannot hide it, flipped above when there is no room below. State is the
  // slot being changed; the markup is added to the band wrapper and removed on close.
  var pickState = null; // { i, trigger }

  function closePicker() {
    var wrap = document.querySelector('[data-kpi-band] .adm-bandwrap');
    if (wrap) {
      Array.prototype.forEach.call(wrap.querySelectorAll('[data-kpi-scrim], [data-kpi-pop]'), function (n) { n.parentNode.removeChild(n); });
      Array.prototype.forEach.call(wrap.querySelectorAll('.adm-ov-kpi-swap.is-on'), function (n) { n.classList.remove('is-on'); });
    }
    var trigger = pickState && pickState.trigger;
    pickState = null;
    return trigger;
  }

  function openPicker(index, trigger) {
    var wrap = trigger.closest('.adm-bandwrap');
    if (!wrap || !bandData || !bandData.metrics) return;
    closePicker();
    var r = trigger.getBoundingClientRect();
    var w = Math.min(380, window.innerWidth - 24);
    var h = Math.min(420, window.innerHeight * 0.6) + 68;
    var below = r.bottom + 6;
    var top = below + h + 12 > window.innerHeight ? Math.max(12, Math.min(r.top - h - 6, window.innerHeight - h - 12)) : below;
    var left = Math.max(12, Math.min(r.right - w, window.innerWidth - w - 12));
    var canRemove = bandSlots().length > 1;
    pickState = { i: index, trigger: trigger };
    trigger.classList.add('is-on');
    var holder = document.createElement('div');
    holder.innerHTML =
      '<div class="adm-metric-scrim" data-kpi-scrim></div>' +
      '<div class="adm-metric-pop" data-kpi-pop style="left:' + Math.round(left) + 'px;top:' + Math.round(top) + 'px;width:' + Math.round(w) + 'px">' +
        '<div class="adm-acct-list adm-metric-list" role="dialog" aria-label="Choose a metric for this slot">' +
          '<input type="text" class="adm-input adm-input-wide" data-kpi-search aria-label="Search metrics" placeholder="Search ' + bandData.metrics.length + ' metrics">' +
          pickListHtml('') +
          '<div class="adm-metric-list-foot">' +
            (canRemove ? '<button type="button" class="adm-btn adm-btn-danger" data-kpi-remove>Remove this slot</button>' : '') +
            '<button type="button" class="adm-btn" data-kpi-close>Close</button>' +
          '</div>' +
        '</div>' +
      '</div>';
    while (holder.firstChild) wrap.appendChild(holder.firstChild);
    var inp = wrap.querySelector('[data-kpi-search]');
    if (inp) inp.focus();
  }

  // Same cross-request race as loadStatusBand() above, same fix: a fresh
  // token per call, checked before either branch writes to the DOM.
  var kpiBandRequestToken = 0;

  function loadBand() {
    var box = document.querySelector('[data-kpi-band]');
    if (!box) return;
    var myToken = ++kpiBandRequestToken;
    // One read: the metrics endpoint carries values, series and breakdowns
    // together since batch 16 - the money-home second fetch is gone.
    adminFetch('/apps/dashboard/admin/metrics').then(function (res) {
      if (myToken !== kpiBandRequestToken) return; // superseded — discard
      if (!res.ok) {
        box.innerHTML = '<p class="admin-status-loading">' + escapeHtml(adminHttpMessage(res)) + '</p>';
        return;
      }
      bandData = { metrics: res.data.metrics };
      renderBand();
    }).catch(function (err) {
      if (myToken !== kpiBandRequestToken) return; // superseded — discard
      box.innerHTML = '<p class="admin-status-loading">' + escapeHtml(adminRejectMessage(err)) + '</p>';
    });
  }

  // The picker: open per slot, type to filter, click to set. Input events
  // re-render the list only, so the search box keeps focus.
  document.addEventListener('input', function (ev) {
    var t = ev.target;
    if (!t.matches || !t.matches('[data-kpi-search]')) return;
    var listBox = t.closest('.adm-metric-list').querySelector('[data-kpi-picklist]');
    if (listBox) listBox.outerHTML = pickListHtml(t.value);
  });
  // Escape and a resize close the popover (it is placed against the button's rect, so a
  // resized window would leave it adrift), and focus goes back to the button that opened it.
  document.addEventListener('keydown', function (ev) {
    if (ev.key !== 'Escape' || !pickState) return;
    var trigger = closePicker();
    if (trigger && trigger.focus) trigger.focus();
  });
  window.addEventListener('resize', function () { if (pickState) closePicker(); });
  document.addEventListener('click', function (ev) {
    if (!ev.target.closest) return;
    var swapBtn = ev.target.closest('[data-kpi-swap-btn]');
    if (swapBtn) {
      var swapIdx = Number(swapBtn.getAttribute('data-kpi-swap-btn'));
      if (pickState && pickState.i === swapIdx) { closePicker(); return; }
      openPicker(swapIdx, swapBtn);
      return;
    }
    if (ev.target.closest('[data-kpi-scrim], [data-kpi-close]')) {
      var back = closePicker();
      if (back && back.focus) back.focus();
      return;
    }
    if (ev.target.closest('[data-kpi-remove]')) {
      var rmSlots = bandSlots();
      if (pickState && rmSlots.length > 1) {
        rmSlots.splice(pickState.i, 1);
        saveSlots(rmSlots);
        renderBand();
      }
      return;
    }
    var pick = ev.target.closest('[data-kpi-pick]');
    if (pick) {
      var idx = pickState ? pickState.i : -1;
      if (idx < 0) return;
      var slots = bandSlots();
      slots[idx] = pick.getAttribute('data-kpi-pick');
      saveSlots(slots);
      renderBand();
      var again = document.querySelector('[data-kpi-swap-btn="' + idx + '"]');
      if (again) again.focus();
      return;
    }
    if (ev.target.closest('[data-kpi-add]')) {
      var addSlots = bandSlots();
      if (addSlots.length < 8 && bandData && bandData.metrics) {
        // The first metric NOT already on the band, preferring one with a live
        // value. The old hardcoded 'ledger-n' added a duplicate once ledger-n
        // was slotted - the audit's A22.
        var unused = bandData.metrics.filter(function (m) { return addSlots.indexOf(m.id) < 0; });
        var pickM = unused.filter(function (m) { return m.available; })[0] || unused[0];
        if (pickM) {
          addSlots.push(pickM.id);
          saveSlots(addSlots);
          renderBand();
        }
      }
      return;
    }
    if (ev.target.closest('[data-kpi-reset]')) {
      saveSlots(BAND_DEFAULTS.slice());
      renderBand();
    }
  });

  loadBand();

  // Re-read on every re-activation: the band and the status tiles both move
  // when entries post elsewhere. See the note in admin-dashboard.js.
  return function () {
    loadStatusBand();
    loadBand();
  };
}
